import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import Button from 'src/components/common/Button/Button'
import Select from 'react-select'
import { GRAY_300, GRAY_400, GRAY_950 } from '../../../scss/colorConsts'
import { getTheme } from '../../../redux/user/userSlice'
import { getPatientRadiologyLabInfo } from '../../../components/common/shared/sharedPatientLogic'
import moment from 'moment'
import { getPCCResultFile } from '../../../helper/pccApiHelper'
import { CSpinner } from '@coreui/react-pro'
import { setPCCLabs } from '../../../redux/progressNote/progressNoteSlice'

const selectStyleDark = {
  control: (styles) => ({
    ...styles,
    backgroundColor: 'var(--reactselect-input-bg-color)',
    color: 'var(--reactselect-input-color)',
    borderColor: GRAY_400,
    boxShadow: 'none',
  }),
  option: (styles, { data, isFocused, isSelected }) => {
    return {
      ...styles,
      backgroundColor: isSelected
        ? 'var(--text-glow-color)'
        : isFocused
          ? 'var(--text-glow-color)'
          : 'var(--reactselect-input-bg-color)',
      color: isSelected ? GRAY_950 : isFocused ? GRAY_950 : data?.color,
      cursor: 'pointer',
      borderBottom: `1px solid ${GRAY_300}`,
      'last-child': {
        border: 'none',
      },
      ':active': {
        ...styles[':active'],
        backgroundColor: 'var(--text-glow-color)',
      },
    }
  },
  singleValue: (styles, { data }) => ({ ...styles, color: 'var(--reactselect-input-color)' }),
  container: (styles) => ({ ...styles, width: '50%' }),
}

const selectStyleLight = {
  control: (styles) => ({
    ...styles,
    backgroundColor: 'var(--reactselect-input-bg-color)',
    color: 'var(--reactselect-input-color)',
    boxShadow: 'none',
  }),
  menu: (styles) => ({ ...styles, backgroundColor: 'var(--reactselect-input-bg-color)' }),
  option: (styles, { data, isDisabled, isFocused, isSelected }) => {
    return {
      ...styles,
      backgroundColor: isSelected
        ? 'var(--text-glow-color)'
        : isFocused
          ? 'var(--text-glow-color)'
          : 'var(--reactselect-input-bg-color)',
      color: 'var(--reactselect-input-color)',
      cursor: 'pointer',
      borderBottom: `1px solid ${GRAY_300}`,
      'last-child': {
        border: 'none',
      },
      ':active': {
        ...styles[':active'],
        backgroundColor: 'var(--text-glow-color)',
      },
    }
  },
  singleValue: (styles, { data }) => ({ ...styles, color: 'var(--reactselect-input-color)' }),
  container: (styles) => ({ ...styles, width: '50%' }),
}

export default function LabPreview() {
  const theme = useSelector(getTheme)
  const dispatch = useDispatch()
  const visitDetails = useSelector((state) => state?.progressNote?.visitDetails)
  const pccLabs = useSelector((state) => state?.progressNote?.pccLabs)

  const [currentLabIndex, setCurrentLabIndex] = useState(0)
  const [isLoading, setIsLoading] = useState(false)

  const labOptions = pccLabs?.slice(0, 10)?.map((lab, index) => {
    return {
      label:
        lab?.reportName +
        ' ' +
        lab?.reportStatus +
        ' ' +
        moment(lab?.effectiveDateTime)?.format('MM/DD/YYYY'),
      value: index,
    }
  })

  useEffect(() => {
    if (pccLabs.length === 0 && visitDetails?.patient_id) {
      setIsLoading(true)
      getPatientRadiologyLabInfo(visitDetails.patient_id, null, (res) => {
        dispatch(setPCCLabs(res.laboratory || []))
        setIsLoading(false)
      })
    }
  }, [visitDetails?.patient_id])

  useEffect(() => {
    if (
      visitDetails?.patient_id &&
      pccLabs[currentLabIndex] &&
      !pccLabs[currentLabIndex]?.url &&
      pccLabs[currentLabIndex]?.reportId
    ) {
      setIsLoading(true)
      getPCCResultFile(visitDetails.patient_id, pccLabs[currentLabIndex].reportId)
        .then((res) => {
          if (res) {
            const url = URL.createObjectURL(res)

            const newLabs = [...pccLabs]
            newLabs[currentLabIndex] = { ...newLabs[currentLabIndex], url }
            dispatch(setPCCLabs(newLabs))
          }
        })
        .finally(() => setIsLoading(false))
    }
  }, [currentLabIndex, pccLabs.length])

  return (
    <div>
      <div
        className={'prior-notes-header'}
        style={{
          display: 'flex',
          gap: '16px',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <Button
          compressed
          style={{
            visibility: currentLabIndex < (pccLabs?.length || 0) - 1 ? 'visible' : 'hidden',
          }}
          onClick={() => {
            if (pccLabs[currentLabIndex + 1]) {
              setCurrentLabIndex(currentLabIndex + 1)
            }
          }}
        >
          Previous
        </Button>

        <Select
          styles={theme === 'light' ? selectStyleLight : selectStyleDark}
          onChange={({ value }) => {
            setCurrentLabIndex(value)
          }}
          value={labOptions.find((option) => option.value === currentLabIndex)}
          options={labOptions}
          isSearchable={false}
        />
        <Button
          compressed
          style={{
            visibility: currentLabIndex > 0 ? 'visible' : 'hidden',
          }}
          onClick={() => {
            if (pccLabs[currentLabIndex - 1]) {
              setCurrentLabIndex(currentLabIndex - 1)
            }
          }}
        >
          Next
        </Button>
      </div>
      {isLoading ? (
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ minHeight: '300px' }}
        >
          <CSpinner />
        </div>
      ) : (
        <div style={{ display: 'flex', overflowY: 'auto', height: '70vh', marginTop: '8px' }}>
          {pccLabs[currentLabIndex]?.url && (
            <iframe
              title={'Lab Viewer'}
              src={pccLabs[currentLabIndex].url + '#toolbar=0&navpanes=0&scrollbar=0'}
              width="100%"
            />
          )}
        </div>
      )}
    </div>
  )
}
